// Self-healing watchdog for the v2 terminal-payment poll.
//
// Problem it fixes: the terminal-payment poll has been observed to stop
// making progress while a payment is pending -- the backend finalizes in
// ~10s but the FE spinner hangs for minutes and only an app re-init clears
// it. Backend logs proved the FE simply stops polling; the device is still
// alive (sibling requests succeed). The candidate causes (React Query paused
// by the offline gate, a fetch wedged in the axios refresh interceptor, the
// interval ceasing to schedule) all share that one symptom, so we recover off
// the symptom rather than waiting to prove the cause:
//
//   onset (no progress for STALL_MS while foreground)
//        -> SOFT: force onlineManager online + reset the poll query
//   still stuck HARD_RECOVER_AFTER_MS later
//        -> HARD: reload once (BE session is durable + refresh-recovery exists)
//
// It ALSO beacons each stall/recovery to /ht/api/status (an existing,
// access-logged endpoint -> same Kibana index) so we still learn the cause.
// The beacon is a raw <img> GET: it bypasses axios + React Query, so it
// survives the very layers that stall the poll. Everything is wrapped so it
// can never throw into checkout.
//
// Decision logic lives in terminalPollWatchdog.core.ts (pure, unit-tested).

import { useEffect, useRef } from 'react'
import { onlineManager, useQueryClient } from '@tanstack/react-query'
import { useSessionStore } from '@/stores/sessionStore'
import { isRefreshingNow } from '@/api/client'
import { evaluateWatchdogTick } from '@/lib/terminalPollWatchdog.core'
import { fireBeacon } from '@/lib/beacon'

// How often the watchdog evaluates. (STALL / heartbeat / recover thresholds
// live in the pure core.)
const CHECK_MS = 3_000

/**
 * Watch the terminal-payment poll for `(receiptId, sessionId)`; beacon and
 * recover if it stalls. Safe to mount unconditionally (no-op until both ids
 * are set).
 */
export function useTerminalPollWatchdog(
  receiptId: number | null,
  sessionId: string | null,
): void {
  const qc = useQueryClient()
  const stalledSinceRef = useRef<number | null>(null)
  const stallAnchorRef = useRef<number>(0)
  const lastBeaconAtRef = useRef<number>(0)
  const beaconCountRef = useRef<number>(0)
  const startedAtRef = useRef<number>(0)
  const becameVisibleAtRef = useRef<number>(0)
  const softRecoverAtRef = useRef<number | null>(null)
  const hardRecoverDoneRef = useRef<boolean>(false)

  useEffect(() => {
    if (receiptId === null || sessionId === null) return
    const start = Date.now()
    startedAtRef.current = start
    becameVisibleAtRef.current =
      typeof document === 'undefined' || !document.hidden ? start : 0
    stalledSinceRef.current = null
    stallAnchorRef.current = 0
    lastBeaconAtRef.current = 0
    beaconCountRef.current = 0
    softRecoverAtRef.current = null
    hardRecoverDoneRef.current = false

    const queryKey = ['terminalPayment', receiptId, sessionId]

    const onVisibility = () => {
      if (typeof document !== 'undefined' && !document.hidden) {
        becameVisibleAtRef.current = Date.now()
      }
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility)
    }

    const tick = () => {
      try {
        const hidden = typeof document !== 'undefined' && document.hidden
        const st = qc.getQueryState(queryKey)
        const data = st?.data as { status?: string } | undefined
        const inProgress = !st || !data || data.status === 'pending'

        const res = evaluateWatchdogTick({
          now: Date.now(),
          hidden,
          inProgress,
          dataUpdatedAt: st?.dataUpdatedAt ?? 0,
          startedAt: startedAtRef.current,
          becameVisibleAt: becameVisibleAtRef.current,
          stalledSince: stalledSinceRef.current,
          stallAnchorUpdatedAt: stallAnchorRef.current,
          lastBeaconAt: lastBeaconAtRef.current,
          beaconCount: beaconCountRef.current,
          softRecoverAt: softRecoverAtRef.current,
          hardRecoverDone: hardRecoverDoneRef.current,
        })

        stalledSinceRef.current = res.stalledSince
        stallAnchorRef.current = res.stallAnchorUpdatedAt
        lastBeaconAtRef.current = res.lastBeaconAt
        beaconCountRef.current = res.beaconCount
        softRecoverAtRef.current = res.softRecoverAt
        hardRecoverDoneRef.current = res.hardRecoverDone

        // --- Diagnostics ---
        if (res.beacon?.kind === 'stall') {
          const { selectedStore, selectedRegister } = useSessionStore.getState()
          fireBeacon({
            diag: 'terminal_stall',
            v: 1,
            action: res.action, // 'soft' on onset, else 'none'
            fetchStatus: st?.fetchStatus ?? 'none',
            status: st?.status ?? 'none',
            fails: st?.fetchFailureCount ?? 0,
            online: onlineManager.isOnline(),
            navOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
            refreshing: isRefreshingNow(),
            sinceUpdateMs: res.beacon.sinceProgressMs,
            stalledMs: res.beacon.stalledMs,
            sid: sessionId,
            rid: receiptId,
            store: selectedStore?.id ?? '',
            register: selectedRegister?.id ?? '',
          })
        } else if (res.beacon?.kind === 'recovered') {
          fireBeacon({
            diag: 'terminal_recovered',
            v: 1,
            outcome: res.beacon.outcome,
            stalledMs: res.beacon.stalledMs,
            fetchStatus: st?.fetchStatus ?? 'none',
            sid: sessionId,
            rid: receiptId,
          })
        }

        // --- Recovery ---
        if (res.action === 'soft') {
          // Un-pause anything gated on the (unreliable) offline flag, then
          // abandon a wedged in-flight fetch and start a fresh one.
          try { onlineManager.setOnline(true) } catch { /* noop */ }
          try { qc.resetQueries({ queryKey }) } catch { /* noop */ }
        } else if (res.action === 'hard') {
          // Last resort: the soft recover didn't take. Reload once. The BE
          // session is durable + idempotent and the app resumes the in-flight
          // session on load, so this recovers into the approved/print state.
          fireBeacon({ diag: 'terminal_hard_recover', v: 1, sid: sessionId, rid: receiptId })
          if (typeof window !== 'undefined') window.location.reload()
        }
      } catch {
        // never throw out of the watchdog
      }
    }

    const h = window.setInterval(tick, CHECK_MS)
    return () => {
      window.clearInterval(h)
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility)
      }
    }
  }, [qc, receiptId, sessionId])
}
